import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { priceCompareAPI } from "@/lib/api";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";

function BrandItem({ b, keyPrefix }) {
  return (
    <a
      href={b.link_url || "/learn/price-compare"}
      className="group flex h-32 w-36 flex-shrink-0 flex-col items-center justify-center gap-3 sm:h-40 sm:w-44"
      data-testid={`brand-card-${keyPrefix}-${b.id}`}
    >
      <img
        src={b.image_url}
        alt={b.name}
        className="h-auto w-auto max-w-[125px] object-contain drop-shadow-[0_6px_14px_rgba(0,0,0,0.55)] transition-transform duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-1 group-hover:scale-[1.06]"
        onError={(e) => { e.currentTarget.style.opacity = 0.25; }}
      />
      <span className="text-center text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground">
        {b.name}
      </span>
    </a>
  );
}

export default function PriceCompareCarousel({ fullBleed = false }) {
  const [brands, setBrands] = useState([]);
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const pausedRef = useRef(false);
  const halfWidthRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    priceCompareAPI
      .getBrandCards()
      .then((res) => { if (!cancelled) setBrands(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setBrands([]); });
    return () => { cancelled = true; };
  }, []);

  // Duplicate the list so the marquee can wrap seamlessly
  const track = brands.length ? [...brands, ...brands] : [];

  // rAF-driven smooth continuous scroll + nudge support
  useEffect(() => {
    if (!brands.length) return;
    const el = trackRef.current;
    if (!el) return;

    const measure = () => { halfWidthRef.current = el.scrollWidth / 2; };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);

    const SPEED = 22; // px/s
    let last = performance.now();
    let raf = 0;

    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!pausedRef.current) {
        offsetRef.current -= SPEED * dt;
        const half = halfWidthRef.current;
        if (half && Math.abs(offsetRef.current) >= half) {
          offsetRef.current += half; // seamless wrap
        }
        el.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [brands.length]);

  const nudge = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const step = 220; // ~ one card width + gap
    offsetRef.current -= dir * step;
    const half = halfWidthRef.current;
    if (half) {
      if (offsetRef.current > 0) offsetRef.current -= half;
      else if (Math.abs(offsetRef.current) >= half) offsetRef.current += half;
    }
    el.style.transition = "transform 400ms cubic-bezier(0.22, 1, 0.36, 1)";
    el.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
    window.setTimeout(() => { if (el) el.style.transition = ""; }, 430);
  };

  return (
    <section
      id="price-compare"
      className="border-y border-border bg-carbon"
      style={fullBleed ? { width: "100vw", marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" } : undefined}
    >
      <div className="src-container mx-auto max-w-7xl px-4 py-24 sm:px-6">
        <div className="mb-10 max-w-2xl">
          <h2 className="relative pl-5 font-display text-[2.27rem] font-bold uppercase leading-[1.05]">
            <span aria-hidden className="absolute left-0 top-1 h-[calc(100%-8px)] w-2 rounded-full bg-primary" />
            Price Compare
          </h2>
          <p className="mt-3 text-[1.15rem]" style={{ color: "#c7c8cf" }}>
            Compare prices, stock and shipping across trusted sim racing brands in one place, so you always know you&apos;re getting the best deal.
          </p>
        </div>

        <div
          className="relative"
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
          onTouchStart={() => { pausedRef.current = true; }}
          onTouchEnd={() => { pausedRef.current = false; }}
        >
          {/* Prev / next arrows */}
          <button
            type="button"
            onClick={() => nudge(-1)}
            aria-label="Scroll left"
            data-testid="price-carousel-prev"
            className="absolute left-2 top-1/2 z-20 hidden -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card/90 p-2 text-foreground backdrop-blur transition-colors hover:border-primary/60 hover:text-primary md:flex"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => nudge(1)}
            aria-label="Scroll right"
            data-testid="price-carousel-next"
            className="absolute right-2 top-1/2 z-20 hidden -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card/90 p-2 text-foreground backdrop-blur transition-colors hover:border-primary/60 hover:text-primary md:flex"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <div className="relative overflow-hidden">
            {/* soft edge fades */}
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-carbon to-transparent sm:w-24" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-carbon to-transparent sm:w-24" />

            {brands.length === 0 ? (
              <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">Loading brands…</div>
            ) : (
              <div
                ref={trackRef}
                className="flex w-max gap-8 will-change-transform sm:gap-12"
                data-testid="price-carousel"
              >
                {track.map((b, idx) => (
                  <BrandItem key={`a-${idx}-${b.id}`} b={b} keyPrefix={`a${idx}`} />
                ))}
                {track.map((b, idx) => (
                  <BrandItem key={`b-${idx}-${b.id}`} b={b} keyPrefix={`b${idx}`} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/learn/price-compare" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="more-brands-link">
            <MoreHorizontal className="h-4 w-4" />
            More brands to compare
          </Link>
          <span className="hidden text-muted-foreground sm:inline">·</span>
          <Link to="/learn/price-compare">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="price-compare-cta">
              Explore price compare <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
